'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import TierDiscountProductMessage from '@/app/_components/TierDiscountProductMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHiveQuickShip } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import Tooltip from '@/app/_components/Tooltip';
import QuantityInput from '@/app/_components/QuantityInput';
import SliderSpinner from '@/app/_components/SliderSpinner';
import Selection from './Selection';
import SubProductSelect from './SubProductSelect';
import SwatchSetContainer from './SwatchSetContainer';
import QuickShipModal from './QuickShipModal';
import VariantImageViewer from './VariantImageViewer';
import TradeProgramCallout from '@/app/_components/TradeProgramCallout';

// Actions
import addToCartAction from '@/app/_lib/actions/AddToCartAction';
import getQuickShipItemsAction from '@/app/_lib/actions/GetQuickShipItemsAction';
import { useAppContext } from '@/app/_lib/_context';
import { useCart } from '@/app/_lib/hooks';
import { trackViewItem, trackAddToCart, gaItem } from '@/app/_lib/analytics/ga4';
const ProductToolbar = ({
  sub_product,
  relatedProducts,
  productState
}) => {
  const {
    openCartModal
  } = useAppContext();
  const {
    refresh
  } = useCart();

  // ==========================================================================
  // EXTRACT VALUES FROM PRODUCT STATE (via useProduct hook)
  // ==========================================================================
  const {
    // Selection state
    selectedOptions,
    isComplete,
    selectionErrors,
    swatchSetErrors,
    // Variant
    currentVariant,
    displaySku,
    // Availability
    availability,
    // Retail pricing
    totalPrice,
    originalTotalPrice,
    optionsCost,
    originalOptionsCost,
    hasPriceRange,
    priceRange,
    originalPriceRange,
    onSale,
    discountPercentage,
    // Trade pricing
    isTradeAccount,
    tradePrice,
    tradePriceRange,
    tradeDiscountRate,
    tradeDiscountOverridden,
    // Lead time
    leadTime,
    optionMessages,
    // Shipping
    shippingMessage,
    // Operations
    selectOption,
    selectSwatch
  } = productState;

  // ==========================================================================
  // LOCAL UI STATE (not part of product configuration)
  // ==========================================================================

  // Miller Knoll suppliers - special display rules (no base price breakdown, no shipping line)
  const isMillerKnoll = sub_product.isMillerKnoll;

  // Cart quantity (separate from product configuration)
  const [quantity, setQuantity] = useState(1);

  // Validation display state
  const [displayErrors, setDisplayErrors] = useState(false);

  // Quick ship UI state
  const [quickShipModalOpen, setQuickShipModalOpen] = useState(false);
  const [quickShipItems, setQuickShipItems] = useState([]);
  const hasQuickShip = sub_product.quickShipSkus?.length > 0;

  // Details section loading animation
  const [detailsUpdating, setDetailsUpdating] = useState(false);
  const detailsUpdateVersion = useRef(0);
  const prevSelectedOptionsRef = useRef(selectedOptions);

  // Trigger details animation when selections change
  useEffect(() => {
    // Skip on initial render
    if (prevSelectedOptionsRef.current === selectedOptions) return;
    detailsUpdateVersion.current += 1;
    const currentVersion = detailsUpdateVersion.current;
    setDetailsUpdating(true);

    // End loading state after 1100ms (if no newer update started)
    const timer = setTimeout(() => {
      if (detailsUpdateVersion.current === currentVersion) {
        setDetailsUpdating(false);
      }
    }, 1100);
    prevSelectedOptionsRef.current = selectedOptions;
    return () => clearTimeout(timer);
  }, [selectedOptions]);

  // GA4 view_item — fire once per product on mount
  const viewItemTracked = useRef(false);
  useEffect(() => {
    if (viewItemTracked.current) return;
    viewItemTracked.current = true;
    const price = parseFloat(sub_product.retail_price) || 0;
    const originalPrice = parseFloat(sub_product.original_price);
    const discount = sub_product.on_sale && originalPrice > price ? originalPrice - price : undefined;
    trackViewItem({
      value: price,
      item: gaItem({
        id: sub_product.sub_product_id,
        name: decodeEntities(sub_product.sub_product_name),
        price,
        quantity: 1,
        brand: sub_product.supplier?.supplier_name,
        discount,
        index: 0
      })
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub_product.sub_product_id]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleCartAdd = useCallback(async () => {
    setDisplayErrors(true);
    if (!isComplete) {
      return false;
    }
    const cartItem = {
      quantity: quantity,
      product: {
        sub_product_id: sub_product.sub_product_id,
        options: selectedOptions.options,
        swatches: selectedOptions.swatches
      }
    };
    const response = await addToCartAction(cartItem);
    if (response.success) {
      // GA4 add_to_cart
      const unitPrice = parseFloat(totalPrice) || 0;
      const originalPrice = parseFloat(sub_product.original_price);
      const discount = sub_product.on_sale && originalPrice > parseFloat(sub_product.retail_price) ? originalPrice - parseFloat(sub_product.retail_price) : undefined;
      trackAddToCart({
        value: unitPrice * quantity,
        item: gaItem({
          id: sub_product.sub_product_id,
          name: decodeEntities(sub_product.sub_product_name),
          price: unitPrice,
          quantity,
          brand: sub_product.supplier?.supplier_name,
          variant: displaySku || undefined,
          discount,
          index: 0
        })
      });
      openCartModal({
        message: 'The item was added to your cart.',
        sub_product: sub_product
      });
      refresh(response.cart);
    }
  }, [quantity, selectedOptions, isComplete, sub_product, openCartModal, refresh, totalPrice, displaySku]);
  const handleQuickShipClick = useCallback(async () => {
    setQuickShipModalOpen(true);
    const response = await getQuickShipItemsAction({
      sub_product_id: sub_product.sub_product_id
    });
    if (response.success) {
      setQuickShipItems(response.data);
    } else {
      setQuickShipItems([]);
    }
  }, [sub_product.sub_product_id]);
  return <>
            <div>
                {relatedProducts && relatedProducts.length > 0 && <SubProductSelect currentSubProduct={sub_product} relatedProducts={relatedProducts} className="mb-6" />}
                {sub_product.selections && sub_product.selections.length > 0 && sub_product.selections.map(selection => <Selection key={selection.selection_id} selection={selection} setOption={selectOption} selectedOptions={selectedOptions.options} selectionErrors={selectionErrors} displayErrors={displayErrors} />)}

                {sub_product.swatchSets && Array.isArray(sub_product.swatchSets) && sub_product.swatchSets.map(swatchSet => <div key={swatchSet.swatch_set_id} className="mb-5">
                            <SwatchSetContainer swatchSet={swatchSet} selectSwatch={selectSwatch} selectedSwatch={selectedOptions.swatches[swatchSet.swatch_set_id]} swatchSetErrors={swatchSetErrors} displayErrors={displayErrors} productInfo={{
          sub_product_id: sub_product.sub_product_id,
          thumb: sub_product.product_photo,
          name: decodeEntities(sub_product.sub_product_name)
        }} />
                        </div>)}
            </div>


            {/* Selected Item Details */}
            <div className="text-right mb-3">

                <div className="font-medium text-base mb-4">
                    Details
                </div>

                {/* Content - fades during updates */}
                <div className={`transition-opacity duration-300 ${detailsUpdating ? 'opacity-30' : 'opacity-100'}`}>
                <div className="mb-4">
                    <VariantImageViewer variant={currentVariant} productName={decodeEntities(sub_product.sub_product_name)} />
                </div>
                {selectedOptions && selectedOptions.options && Object.entries(selectedOptions.options).map(([key, option], index) => {
          if (!option) return null;
          return <div key={index} className="text-xs text-right mb-1">
                                {option.selection_label && <span className="text-3xs text-light-3 me-1 capitalize">{option.selection_label}:</span>}
                                {option.option_name && <span className="capitalize">{option.option_name}</span>}
                                {option.option_price > 0 && <div className="">
                                        <span className="">+ <Price amount={option.option_price} /></span>
                                        {option.original_option_price && <span className="line-through text-light ml-1">+ <Price amount={option.original_option_price} /></span>}
                                    </div>}
                            </div>;
        })}
                {selectedOptions && selectedOptions.swatches && Object.entries(selectedOptions.swatches).map(([key, swatch], index) => {
          if (!swatch) return null;
          return <div key={index} className="text-xs text-right mb-1">
                                {swatch.swatch_set_label && <span className="text-3xs text-light-3 me-1 capitalize">{swatch.swatch_set_label}:</span>}
                                {swatch.swatch_name && <span className="capitalize">{swatch.swatch_name}</span>}
                                {swatch.effective_price > 0 && <div className="text-light">
                                        <span>+ <Price amount={swatch.effective_price} /></span>
                                        {swatch.original_effective_price > swatch.effective_price && <span className="line-through text-light ml-1">+ <Price amount={swatch.original_effective_price} /></span>}
                                    </div>}
                            </div>;
        })}
                {displaySku && <div className="text-xs text-right mb-1">
                        <span className="text-3xs text-light-3">SKU:</span> {displaySku}
                    </div>}
                {/* Lead Time Display - Priority: backorder > optionMessages > leadTime (only if options complete) > "select options" */}
                {sub_product.custom_backorder_message ? <div className="text-xs text-right mb-1">
                        <div className="text-red font-medium">{sub_product.production_hold ? 'production hold' : 'backordered'}</div>
                        <div>{sub_product.backorder_message}</div>
                    </div> : sub_product.backordered && sub_product.backorder_message ? <div className="text-xs text-right mb-1">
                        <div className="text-red font-medium">backordered</div>
                        <div>{sub_product.backorder_message}</div>
                    </div> : optionMessages ? <div className="text-xs text-right mb-1">
                        <div className="text-red font-medium">{optionMessages.option_prompt}</div>
                        <div>{optionMessages.option_message}</div>
                    </div> : isComplete && leadTime ? <div className="mb-1">
                        <div className="text-xs text-right">
                            <span className="text-3xs text-light-3">Lead Time:</span>
                            <span className="ml-1 capitalize">{leadTime}</span>
                            <Tooltip content={<div className="max-w-64">Lead time is a good faith estimate.</div>} side="left">
                                <span className="ml-1 w-3 h-3 rounded-full bg-gray-20 text-white text-4xs font-mono font-medium inline-flex items-center justify-center hover:bg-primary transition-colors cursor-pointer">
                                    i
                                </span>
                            </Tooltip>
                        </div>
                    </div> : <div className="text-xs text-right mb-1">
                        Select options for price &amp; lead time
                    </div>}
                {/* Availability messages for selected options/swatches */}
                {availability?.messages?.length > 0 && <div className="mt-2">
                        {availability.messages.map((msg, idx) => <div key={idx} className="text-xs text-right mb-1">
                                {typeof msg === 'string' ? <span className="text-red">{msg}</span> : msg.label ? <>
                                        <div className="text-red">{msg.label}</div>
                                        <div>{msg.dateMessage}</div>
                                    </> : <span className="text-red">{msg.name}: {msg.message}</span>}
                            </div>)}
                    </div>}
                </div>

                {/* Slider below content */}
                <div className={`mt-3 transition-opacity duration-200 ${detailsUpdating ? 'opacity-100' : 'opacity-0'}`}>
                    <SliderSpinner height={1} />
                </div>
            </div>
            {hasQuickShip && <div className="flex justify-end mb-6">
                    <button className="flex items-center" onClick={handleQuickShipClick}>
                        <span className="font-medium text-xs mr-1 text-primary">View Quick Ship Options</span>
                        <FontAwesomeIcon icon={faHiveQuickShip} className="text-3xl text-primary" />
                    </button>
                </div>}
            <div className="border-t border-gray-8 mb-6"></div>
            {onSale && discountPercentage > 0 && <div className="text-sm text-red text-right mb-1">
                    {/* Tier spend promotion, not a sale */}
                    {!sub_product.is_tier_promo && <span className="font-semibold">Sale </span>}{discountPercentage}% off
                </div>}
            {/* Hide base price and options breakdown for Miller Knoll suppliers */}
            {!isMillerKnoll && <>
                    {/* Base Price - commented out per MAP compliance (total shown at top)
                     <div className="flex items-start justify-between mb-2 md:mb-1">
                        <div className="text-sm">
                            Base Price
                        </div>
                        <div className="text-md text-right">
                            {(hasPriceRange && !isComplete) ? (
                                <>
                                    <div>{toCurrency(priceRange.min)} - {toCurrency(priceRange.max)}</div>
                                    {originalPriceRange && (
                                        <div className="line-through text-light-3 text-xs">
                                            {toCurrency(originalPriceRange.min)} - {toCurrency(originalPriceRange.max)}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span>{toCurrency(sub_product.retail_price)}</span>
                                    {sub_product.original_price && (
                                        <span className="line-through text-light-3 text-xs ml-1">{toCurrency(sub_product.original_price)}</span>
                                    )}
                                </>
                            )}
                        </div>
                     </div>
                     */}
                    {optionsCost > 0 && <div className="flex items-center justify-between mb-2 md:mb-1">
                            <div className="text-sm">
                                Options
                            </div>
                            <div className="text-md font-medium md:font-normal">
                                <span>+ <Price amount={optionsCost} /></span>
                                {originalOptionsCost > optionsCost && <span className="line-through text-light ml-2">+ <Price amount={originalOptionsCost} /></span>}
                            </div>
                        </div>}
                </>}
            {!isMillerKnoll && <div className="flex items-center justify-between mb-2 md:mb-2">
                    <div className="text-sm">
                        Shipping Cost
                    </div>
                    <div className="!text-xs">
                        {shippingMessage || 'TBD'}
                    </div>
                </div>}
            <div className="flex items-end justify-between mb-2 md:mb-1">
                <div className="text-sm leading-none">
                    Total
                </div>
                <div className="text-right">
                    {hasPriceRange && !isComplete ? <>
                            <div className="font-medium text-xl leading-none">
                                <Price amount={priceRange.min} /> - <Price amount={priceRange.max} />
                            </div>
                            {originalPriceRange && <div className="line-through text-light text-xs">
                                    <Price amount={originalPriceRange.min} /> - <Price amount={originalPriceRange.max} />
                                </div>}
                        </> : <>
                            <Price amount={totalPrice} className="font-medium text-xl leading-none" />
                            {originalTotalPrice && <Price amount={originalTotalPrice} className="line-through text-light text-xs ml-2" />}
                        </>}
                </div>
            </div>
            <TradeProgramCallout className="mt-6" isComplete={isComplete} onSale={onSale} isTradeAccount={isTradeAccount} tradePrice={tradePrice} tradePriceRange={tradePriceRange} tradeDiscountRate={tradeDiscountRate} tradeDiscountOverridden={tradeDiscountOverridden} />
            <div className="mb-6 mt-8">
                <QuickShipModal isOpen={quickShipModalOpen} setIsOpen={setQuickShipModalOpen} sub_product={sub_product} quickShipItems={quickShipItems} />
            </div>
            {displayErrors && !isComplete && <div className="flex gap-2 mb-2">
                    <div className="w-1/3"></div>
                    <div className="w-2/3 text-red text-sm">
                        Please complete option choices
                    </div>
                </div>}
            <TierDiscountProductMessage supplier_id={sub_product.supplier_id} />
            <div className="flex items-stretch mb-8 gap-2">
                <div className="w-1/3">
                    <QuantityInput quantity={quantity} setQuantity={setQuantity} input_padding={"py-2.5"} disabled={!availability?.canPurchase} />
                </div>
                <div className="w-2/3">
                    {!availability?.canPurchase ? <div className="btn btn-xs h-full btn-disabled bg-gray-10 text-light cursor-not-allowed flex items-center justify-center">
                            {sub_product.button_replacement_text || 'Unavailable'}
                        </div> : <input className={`btn btn-xs h-full ${isComplete ? 'btn-primary' : 'btn-default'}`} type="submit" value="Add to Cart" onClick={() => handleCartAdd()} />}
                </div>
            </div>
        </>;
};
export default ProductToolbar;